import { useApolloClient } from '@apollo/client'
import { useEffectReducer } from 'use-effect-reducer'
import { TAG, useTrackLSPAYError } from '../errorTracking'
import { useTrackLSPAYTiming } from '../timeTracking'
import { useShouldUseSimulator } from '../useShouldUseSimulator'
import { useVdFeatureManager } from '../../../../../ng-compat/hooks'
import { SANDBOX_FLAG } from '../../helpers/lspayConstants'
import { getRefundEffectsMap } from './effects'
import { refundStateReducer } from './reducer'
import { RefundReducerArgs, RefundState, UnrefRefundReason } from './types'

export const useRefundEffectReducer = (args: RefundReducerArgs) => {
  const {
    retailerPaymentType,
    originalRegisterSalePaymentIds,
    originalSalePaidByOtherPT,
    originalLspayPaymentAmount,
    amount,
    transactionId,
  } = args

  const trackError = useTrackLSPAYError(TAG.refund, {
    transactionId,
    originalRegisterSalePaymentIds,
  })
  const trackTiming = useTrackLSPAYTiming({
    transactionId,
    paymentTypeId: retailerPaymentType.type_id,
    isRefund: amount.isNegative(),
  })
  const shouldUseSimulator = useShouldUseSimulator()
  const isSandbox = useVdFeatureManager().get(SANDBOX_FLAG)

  const apolloClient = useApolloClient()

  const effectReducer = useEffectReducer(
    refundStateReducer,
    // initial state
    exec => {
      // blind refund
      if (
        !originalRegisterSalePaymentIds ||
        originalRegisterSalePaymentIds.length < 1
      ) {
        const unrefReason = originalSalePaidByOtherPT
          ? UnrefRefundReason.DIFFERENT_PAYMENT_TYPES
          : UnrefRefundReason.BLIND_REFUND
        return {
          status: 'unreferencedPrompt',
          retailerPaymentType,
          amount,
          errorMessage: undefined,
          dismissible: true,
          unrefReason,
        } as RefundState
      }

      // refund multiple LSPay payments
      if (originalRegisterSalePaymentIds.length > 1) {
        return {
          status: 'unreferencedPrompt',
          retailerPaymentType,
          amount,
          errorMessage: undefined,
          dismissible: true,
          unrefReason: UnrefRefundReason.MULTIPLE_PAYMENTS,
        } as RefundState
      }

      // Split-tender detection: If refund amount exceeds the original LSPay payment amount,
      // show the amount exceeded prompt immediately rather than letting the API call fail.
      // This handles cases like: Sale = LSPay $20 + Cash $14.78, Refund = $34.78
      // The user can then choose to refund just the $20 (CNP) or the full amount (card present).
      if (
        originalLspayPaymentAmount !== undefined &&
        amount.abs().toNumber() > originalLspayPaymentAmount
      ) {
        return {
          status: 'amountExceededPrompt',
          retailerPaymentType,
          amount,
          refundableAmount: String(originalLspayPaymentAmount),
          dismissible: true,
        } as RefundState
      }

      // else perform card not present refund
      // This attempts a CNP refund using the legacy API endpoint.
      // For Processing Service payments, this will refund to the original payment method.
      // Note: Split-tender scenarios are typically caught by the upfront check above (lines 69-85).
      // However, if originalLspayPaymentAmount is unavailable, a backend error may still occur
      // when the refund amount exceeds the original payment amount, triggering amountExceededPrompt
      // as a fallback.
      exec({
        type: 'cardNotPresentEffect',
      })
      return {
        status: 'refundingViaCardNotPresent',
        errorMessage: undefined,
        retailerPaymentType,
        retryable: false,
      } as RefundState
    },
    getRefundEffectsMap({
      ...args,
      trackError,
      trackTiming,
      isSimulated: shouldUseSimulator,
      apolloClient,
      isSandbox,
    })
  )

  return effectReducer
}
